import {useEffect, useState, useMemo, useRef} from "react";
import {useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import moment from "moment";

import DownloadIcon from "@mui/icons-material/Download";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFSelectNew from "@/components/Input/SelectNew";
import PlayerRadarChart from "@/components/PlayerRadarChart";

import {PlayerAPI} from "@/api/player";
import {PlayerValuationAPI} from "@/api/player_valuation";

import {getPlayerStructure} from "@/utils/functions";
import {
	ERROR_OPERATION,
	SUCCESSFUL_OPERATION,
	initialValuationParameters,
	initialBaseValuationParameters,
} from "@/utils/constants";
import {useCategories} from "@/utils/hooks";
import {generateComparisonPDF} from "@/utils/generateComparisonPDF";

import {COLORS as STYLE} from "@/style/utils";

import "./index.scss";

const MAX_PLAYERS = 2;
// Colori dei due giocatori confrontati (anche nei grafici)
const COLORS = [STYLE.primary, STYLE.good];

const ROLE_BADGE = {
	Portiere: {short: "POR", cls: "gk"},
	Difensore: {short: "DIF", cls: "def"},
	Mediano: {short: "MED", cls: "dmid"},
	Centrocampista: {short: "CEN", cls: "mid"},
	Trequartista: {short: "TRE", cls: "amid"},
	Attaccante: {short: "ATT", cls: "att"},
};

const getRoleBadge = (player) => {
	if (player?.goalkeeper) return ROLE_BADGE.Portiere;
	return ROLE_BADGE[player?.primaryRole] ?? {short: "—", cls: "default"};
};

// ── Player info card ──────────────────────────────────────────────────────────

function PlayerInfoCard({player, color, index}) {
	if (!player) return null;

	const badge = getRoleBadge(player);
	const age = player.dateOfBirth
		? moment().diff(moment(player.dateOfBirth), "years")
		: null;

	return (
		<div className="pc-player-card" style={{"--accent": color}}>
			<div className="pc-player-card__header">
				<span className="pc-player-card__index">{index + 1}</span>
				<div className="pc-player-card__name-block">
					<span className="pc-player-card__name">
						{player.firstName} {player.lastName}
					</span>
					<span className="pc-player-card__category">
						{player.playerCategoryName || "—"}
					</span>
				</div>
				<span className={"pc-role-badge pc-role-badge--" + badge.cls}>
					{badge.short}
				</span>
			</div>
			<div className="pc-player-card__stats">
				{age && (
					<div className="pc-player-card__stat">
						<span className="pc-player-card__stat-value">{age}</span>
						<span className="pc-player-card__stat-label">Anni</span>
					</div>
				)}
				{player.lastHeight > 0 && (
					<div className="pc-player-card__stat">
						<span className="pc-player-card__stat-value">
							{player.lastHeight}
						</span>
						<span className="pc-player-card__stat-label">cm</span>
					</div>
				)}
				{player.lastWeight > 0 && (
					<div className="pc-player-card__stat">
						<span className="pc-player-card__stat-value">
							{player.lastWeight}
						</span>
						<span className="pc-player-card__stat-label">kg</span>
					</div>
				)}
				{player.favoriteFoot && (
					<div className="pc-player-card__stat">
						<span className="pc-player-card__stat-value">
							{player.favoriteFoot}
						</span>
						<span className="pc-player-card__stat-label">Piede</span>
					</div>
				)}
				{player.nationality && (
					<div className="pc-player-card__stat">
						<span className="pc-player-card__stat-value">
							{player.nationality}
						</span>
						<span className="pc-player-card__stat-label">Nazionalità</span>
					</div>
				)}
			</div>
		</div>
	);
}

// ── Comparison table ──────────────────────────────────────────────────────────

function ComparisonTable({areasA, areasB, nameA, nameB}) {
	const allAreas = useMemo(() => {
		const keys = new Set([...Object.keys(areasA), ...Object.keys(areasB)]);
		return Array.from(keys).map((area) => ({
			area: String(area).charAt(0).toUpperCase() + String(area).slice(1),
			valueA: areasA[area] ?? null,
			valueB: areasB[area] ?? null,
		}));
	}, [areasA, areasB]);

	if (!allAreas.length) return null;

	return (
		<div className="pc-table">
			<div className="pc-table__header">
				<span className="pc-table__col pc-table__col--area">Area</span>
				<span
					className="pc-table__col pc-table__col--value"
					style={{color: COLORS[0]}}
				>
					{nameA}
				</span>
				<span
					className="pc-table__col pc-table__col--value"
					style={{color: COLORS[1]}}
				>
					{nameB}
				</span>
				<span className="pc-table__col pc-table__col--diff">Diff</span>
			</div>
			{allAreas.map((row) => {
				const diff =
					row.valueA !== null && row.valueB !== null
						? row.valueA - row.valueB
						: null;
				return (
					<div key={row.area} className="pc-table__row">
						<span className="pc-table__col pc-table__col--area">
							{row.area}
						</span>
						<span className="pc-table__col pc-table__col--value">
							{row.valueA !== null ? row.valueA.toFixed(1) : "—"}
						</span>
						<span className="pc-table__col pc-table__col--value">
							{row.valueB !== null ? row.valueB.toFixed(1) : "—"}
						</span>
						<span
							className={
								"pc-table__col pc-table__col--diff" +
								(diff > 0
									? " pc-table__col--pos"
									: diff < 0
										? " pc-table__col--neg"
										: "")
							}
						>
							{diff !== null
								? (diff > 0 ? "+" : "") + diff.toFixed(1)
								: "—"}
						</span>
					</div>
				);
			})}
		</div>
	);
}

// ── Main page ─────────────────────────────────────────────────────────────────

function PlayersComparisonPage(props) {
	const {handleNotification} = props;
	const {t} = useTranslation();

	const seasonId = useSelector((state) => state?.season?.id);
	const {categories} = useCategories({
		setLoading: false,
		handleNotification,
	});

	const [loading, setLoading] = useState(false);
	const [pdfLoading, setPdfLoading] = useState(false);
	const [players, setPlayers] = useState([]);
	const [selectedPlayers, setSelectedPlayers] = useState([null, null]);
	const [playersData, setPlayersData] = useState({});
	const [valuationsDataMap, setValuationsDataMap] = useState({});

	const individualChartRefs = useRef({});
	const combinedChartRef = useRef(null);

	useEffect(() => {
		const load = async () => {
			setLoading(true);
			try {
				const response = await PlayerAPI.list({
					seasonId,
					prospect: false,
				});
				setPlayers(response || []);
			} catch {
				handleNotification(ERROR_OPERATION, "error");
			} finally {
				setLoading(false);
			}
		};
		load();
	}, [seasonId]);

	const formattedPlayers = useMemo(() => {
		return players
			.map((p) => ({
				value: p.id,
				label: `${p.firstName} ${p.lastName}${p.playerCategory?.name ? ` (${p.playerCategory.name})` : ""}`,
				sortKey: `${p.lastName} ${p.firstName}`,
			}))
			.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
	}, [players]);

	const fetchPlayerData = async (playerId) => {
		try {
			const [playerData, valuations] = await Promise.all([
				PlayerAPI.getById(playerId, {seasonId}),
				PlayerValuationAPI.playerList(playerId, {seasonId}),
			]);

			let valuationsData = [];
			if (valuations?.length) {
				valuationsData = await Promise.all(
					valuations.map((v) => PlayerValuationAPI.getById(v.id)),
				);
			}

			return {playerData, valuationsData};
		} catch {
			handleNotification(ERROR_OPERATION, "error");
			return {playerData: null, valuationsData: []};
		}
	};

	const handlePlayerSelect = async (index, selectedValue) => {
		const newSelected = [...selectedPlayers];

		if (selectedValue) {
			const otherIndex = index === 0 ? 1 : 0;
			if (newSelected[otherIndex]?.value === selectedValue.value) {
				handleNotification("Giocatore già selezionato", "warning");
				return;
			}
			newSelected[index] = selectedValue;
		} else {
			newSelected[index] = null;
		}

		setSelectedPlayers(newSelected);

		if (selectedValue && !playersData[selectedValue.value]) {
			setLoading(true);
			try {
				const {playerData, valuationsData} = await fetchPlayerData(
					selectedValue.value,
				);
				setPlayersData((prev) => ({
					...prev,
					[selectedValue.value]: playerData,
				}));
				setValuationsDataMap((prev) => ({
					...prev,
					[selectedValue.value]: valuationsData,
				}));
			} finally {
				setLoading(false);
			}
		}
	};

	// ── Compute area averages for comparison table ────────────────────────────
	const computeAreaAverages = (playerData, valuationsData) => {
		if (!playerData || !valuationsData?.length) return {};

		const playerCategory = categories?.find(
			(c) => c.id === playerData.playerCategoryId,
		);
		const avgAreas = {};

		valuationsData.forEach((v) => {
			const parameters =
				!playerCategory?.basicActivities ||
				playerData.playerCategoryName === "Under 13"
					? initialValuationParameters(t)
					: initialBaseValuationParameters(t);

			parameters.forEach((p) => {
				if (!avgAreas[p.area]) avgAreas[p.area] = {total: 0, count: 0};
				if (v[p.fieldKeyRating]) {
					avgAreas[p.area].total += v[p.fieldKeyRating];
					avgAreas[p.area].count += 1;
				}
			});
		});

		const result = {};
		Object.keys(avgAreas).forEach((area) => {
			result[area] = avgAreas[area].count
				? avgAreas[area].total / avgAreas[area].count
				: 0;
		});

		result["struttura"] = getPlayerStructure(
			playerData.playerCategoryName || "",
			playerData.lastHeight,
			playerData.lastWeight,
		);

		return result;
	};

	const combinedRadarData = useMemo(() => {
		const active = selectedPlayers.filter(Boolean);
		if (active.length < 2) return [];

		const allRadarData = {};
		active.forEach((player) => {
			const pd = playersData[player.value];
			const vd = valuationsDataMap[player.value];
			if (!pd || !vd?.length) return;

			const areas = computeAreaAverages(pd, vd);
			allRadarData[player.value] = areas;
		});

		if (Object.keys(allRadarData).length < 2) return [];

		const allSubjects = new Set();
		Object.values(allRadarData).forEach((areas) => {
			Object.keys(areas).forEach((a) => allSubjects.add(a));
		});

		return Array.from(allSubjects).map((area) => {
			const label = String(area).charAt(0).toUpperCase() + String(area).slice(1);
			const point = {subject: label, fullMark: 10};
			active.forEach((player, i) => {
				point[`player${i + 1}`] = allRadarData[player.value]?.[area] || 0;
			});
			return point;
		});
	}, [selectedPlayers, playersData, valuationsDataMap, categories, t]);

	const areasA = useMemo(() => {
		const p = selectedPlayers[0];
		if (!p) return {};
		return computeAreaAverages(
			playersData[p.value],
			valuationsDataMap[p.value],
		);
	}, [selectedPlayers, playersData, valuationsDataMap, categories, t]);

	const areasB = useMemo(() => {
		const p = selectedPlayers[1];
		if (!p) return {};
		return computeAreaAverages(
			playersData[p.value],
			valuationsDataMap[p.value],
		);
	}, [selectedPlayers, playersData, valuationsDataMap, categories, t]);

	// ── PDF ──────────────────────────────────────────────────────────────────
	const handleDownloadPDF = async () => {
		const active = selectedPlayers.filter(Boolean);
		if (!active.length) {
			handleNotification("Seleziona almeno un giocatore", "warning");
			return;
		}

		setPdfLoading(true);
		try {
			const pdfGenerator = await generateComparisonPDF(
				active,
				playersData,
				valuationsDataMap,
				combinedRadarData,
				t,
			);

			for (let i = 0; i < active.length; i++) {
				const chartElement =
					individualChartRefs.current[active[i]?.value];
				if (chartElement) {
					await pdfGenerator.addIndividualChart(chartElement, i);
				}
			}

			if (active.length > 1 && combinedChartRef.current) {
				await pdfGenerator.addCombinedChart(combinedChartRef.current);
			}

			pdfGenerator.save();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(
				error.message || "Errore nella generazione del PDF",
				"error",
			);
		} finally {
			setPdfLoading(false);
		}
	};

	// ── Derived state ────────────────────────────────────────────────────────
	const activePlayers = selectedPlayers.filter(Boolean);
	const bothSelected = activePlayers.length === 2;
	const hasAnyData = activePlayers.some(
		(p) => valuationsDataMap[p.value]?.length,
	);

	const headerActions = [
		{
			icon: <DownloadIcon />,
			title: pdfLoading ? "Generazione PDF..." : "Scarica PDF",
			onClick: handleDownloadPDF,
			disabled: !activePlayers.length || !hasAnyData,
		},
	];

	return (
		<Page className="pc-page">
			<Loading visible={loading} />
			<Breadcrumbs
				current="Confronta giocatori"
				actions={<MainActions actions={headerActions} />}
			/>

			{/* Select giocatori */}
			<div className="pc-selectors">
				{Array.from({length: MAX_PLAYERS}).map((_, index) => (
					<div key={index} className="pc-selectors__item">
						<div
							className="pc-selectors__dot"
							style={{background: COLORS[index]}}
						/>
						<div className="pc-selectors__select">
							<VLFSelectNew
								options={formattedPlayers}
								sortKey="sortKey"
								value={selectedPlayers[index]}
								onChange={(value) => handlePlayerSelect(index, value)}
								label={`Giocatore ${index + 1}`}
								isClearable
							/>
						</div>
					</div>
				))}
			</div>

			{/* Stato vuoto */}
			{!activePlayers.length && (
				<div className="pc-empty">
					<CompareArrowsIcon sx={{fontSize: 48, color: "var(--color-text-muted)"}} />
					<span className="pc-empty__title">Confronto giocatori</span>
					<span className="pc-empty__text">
						Seleziona due giocatori dai menu sopra per confrontare le loro
						valutazioni, statistiche e radar chart.
					</span>
				</div>
			)}

			{/* Schede giocatori */}
			{activePlayers.length > 0 && (
				<div className="pc-cards">
					{selectedPlayers.map((sel, index) => {
						if (!sel) return null;
						const pd = playersData[sel.value];
						if (!pd) return null;
						return (
							<PlayerInfoCard
								key={sel.value}
								player={pd}
								color={COLORS[index]}
								index={index}
							/>
						);
					})}
				</div>
			)}

			{/* Avviso valutazioni mancanti */}
			{activePlayers.length > 0 && (
				<div className="pc-warnings">
					{activePlayers.map((p, i) => {
						const vd = valuationsDataMap[p.value];
						if (vd?.length) return null;
						const pd = playersData[p.value];
						if (!pd) return null;
						return (
							<div key={p.value} className="pc-warning">
								<InfoOutlinedIcon sx={{fontSize: 16, color: "var(--color-warning)"}} />
								<span>
									<strong>
										{pd.firstName} {pd.lastName}
									</strong>{" "}
									non ha valutazioni per questa stagione. Inserisci una
									valutazione dalla scheda del giocatore per abilitare il
									confronto.
								</span>
							</div>
						);
					})}
				</div>
			)}

			{/* Radar individuali */}
			{hasAnyData && (
				<div className="pc-section">
					<div className="pc-section__title">Radar individuali</div>
					<div className="pc-radars">
						{activePlayers.map((player, index) => {
							const pd = playersData[player.value];
							const vd = valuationsDataMap[player.value] || [];
							const globalIndex = selectedPlayers.indexOf(player);

							return (
								<div key={player.value} className="pc-radars__item">
									<div
										className="pc-radars__label"
										style={{color: COLORS[globalIndex]}}
									>
										{pd?.firstName} {pd?.lastName}
									</div>
									<div
										className="chart-wrapper-for-pdf"
										ref={(el) => {
											if (el) {
												individualChartRefs.current[player.value] =
													el.querySelector(
														".player-radar-chart-container",
													) || el;
											}
										}}
									>
										{vd.length ? (
											<PlayerRadarChart
												playerData={pd}
												valuationsData={vd}
												color={COLORS[globalIndex]}
											/>
										) : (
											<div className="pc-radars__empty">
												Nessuna valutazione disponibile
											</div>
										)}
									</div>
								</div>
							);
						})}
					</div>
				</div>
			)}

			{/* Radar combinato */}
			{bothSelected && combinedRadarData.length > 0 && (
				<div className="pc-section">
					<div className="pc-section__title">Radar comparativo</div>
					<div className="pc-combined">
						<div
							className="chart-wrapper-for-pdf"
							ref={(el) => {
								if (el) {
									combinedChartRef.current =
										el.querySelector(".player-radar-chart-container") || el;
								}
							}}
						>
							<PlayerRadarChart
								multiPlayerData={combinedRadarData}
								players={activePlayers.map((p, i) => {
									const globalIndex = selectedPlayers.indexOf(p);
									return {
										name:
											playersData[p.value]
												? `${playersData[p.value].firstName} ${playersData[p.value].lastName}`
												: `Giocatore ${i + 1}`,
										color: COLORS[globalIndex],
									};
								})}
							/>
						</div>
					</div>
				</div>
			)}

			{/* Tabella comparativa */}
			{bothSelected &&
				Object.keys(areasA).length > 0 &&
				Object.keys(areasB).length > 0 && (
					<div className="pc-section">
						<div className="pc-section__title">Confronto per area</div>
						<ComparisonTable
							areasA={areasA}
							areasB={areasB}
							nameA={
								playersData[selectedPlayers[0]?.value]?.lastName || "Giocatore 1"
							}
							nameB={
								playersData[selectedPlayers[1]?.value]?.lastName || "Giocatore 2"
							}
						/>
					</div>
				)}
		</Page>
	);
}

export default PlayersComparisonPage;
